import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  EngineeringAgentView,
  EngineeringSession,
  EngineeringTaskView,
  ExecutionArtifact,
  ExecutionPlan,
  ExecutionRun,
  ExecutionStep,
  OrchestrationRun,
  RepositoryChangeset,
  TimelineEntry,
  ValidationPipelineView,
} from '../types/engineering';
import { apiV2Client } from './api';
import { pollIntervalMs } from '../lib/queryPolling';

/** Session-authenticated `/api/v2` fetch (JWT wins; automation key only as fallback). */
const engFetch = <T,>(path: string, init?: RequestInit) => apiV2Client<T>(path, init);

function q(projectId: string, extra?: Record<string, string>) {
  const p = new URLSearchParams({ project_id: projectId, ...extra });
  return `?${p.toString()}`;
}

export const engineeringKeys = {
  sessions: (projectId: string, state?: string) => ['engineering', 'sessions', projectId, state] as const,
  session: (projectId: string, sessionId: string) => ['engineering', 'session', projectId, sessionId] as const,
  plan: (projectId: string, planId: string) => ['engineering', 'plan', projectId, planId] as const,
  planGraph: (projectId: string, planId: string) => ['engineering', 'plan-graph', projectId, planId] as const,
  orch: (projectId: string, orchId: string) => ['engineering', 'orch', projectId, orchId] as const,
  run: (projectId: string, runId: string) => ['engineering', 'run', projectId, runId] as const,
  artifacts: (projectId: string, runId: string) => ['engineering', 'artifacts', projectId, runId] as const,
  timeline: (projectId: string, runId: string) => ['engineering', 'timeline', projectId, runId] as const,
  steps: (projectId: string, runId: string) => ['engineering', 'steps', projectId, runId] as const,
  sessionTimeline: (projectId: string, sessionId: string) =>
    ['engineering', 'session-timeline', projectId, sessionId] as const,
  validation: (projectId: string, sessionId: string) =>
    ['engineering', 'validation', projectId, sessionId] as const,
  changeset: (projectId: string, sessionId: string) =>
    ['engineering', 'changeset', projectId, sessionId] as const,
  agents: (projectId: string, sessionId: string) => ['engineering', 'agents', projectId, sessionId] as const,
};

export function useEngineeringSessions(projectId: string, state?: string) {
  return useQuery({
    queryKey: engineeringKeys.sessions(projectId, state),
    queryFn: () =>
      engFetch<{ sessions: EngineeringSession[]; count: number }>(
        `/engineering/sessions${q(projectId, state ? { state } : undefined)}`,
      ),
    enabled: Boolean(projectId),
    refetchInterval: () => pollIntervalMs(15_000),
  });
}

export function useEngineeringSession(projectId: string, sessionId?: string) {
  return useQuery({
    queryKey: engineeringKeys.session(projectId, sessionId ?? ''),
    queryFn: () => engFetch<EngineeringSession>(`/engineering/sessions/${sessionId}${q(projectId)}`),
    enabled: Boolean(projectId && sessionId),
    refetchInterval: () => pollIntervalMs(8_000),
  });
}

export function useExecutionPlan(projectId: string, planId?: string) {
  return useQuery({
    queryKey: engineeringKeys.plan(projectId, planId ?? ''),
    queryFn: async () => {
      const plan = await engFetch<ExecutionPlan>(`/engineering/plans/${planId}${q(projectId)}`);
      try {
        const graph = await engFetch<ExecutionPlan['graph']>(
          `/engineering/plans/${planId}/graph${q(projectId)}`,
        );
        if (graph && 'nodes' in (graph as object)) plan.graph = graph as ExecutionPlan['graph'];
      } catch {
        /* graph optional */
      }
      return plan;
    },
    enabled: Boolean(projectId && planId),
    refetchInterval: () => pollIntervalMs(10_000),
  });
}

export function useOrchestration(projectId: string, orchId?: string) {
  return useQuery({
    queryKey: engineeringKeys.orch(projectId, orchId ?? ''),
    queryFn: () => engFetch<OrchestrationRun>(`/engineering/orchestration/${orchId}${q(projectId)}`),
    enabled: Boolean(projectId && orchId),
    refetchInterval: () => pollIntervalMs(10_000),
  });
}

export function useExecutionRun(projectId: string, runId?: string) {
  return useQuery({
    queryKey: engineeringKeys.run(projectId, runId ?? ''),
    queryFn: () => engFetch<ExecutionRun>(`/engineering/execution/${runId}${q(projectId)}`),
    enabled: Boolean(projectId && runId),
    refetchInterval: () => pollIntervalMs(5_000),
  });
}

export function useExecutionArtifacts(projectId: string, runId?: string) {
  return useQuery({
    queryKey: engineeringKeys.artifacts(projectId, runId ?? ''),
    queryFn: () =>
      engFetch<{ artifacts: ExecutionArtifact[] }>(
        `/engineering/execution/${runId}/artifacts${q(projectId)}`,
      ).then((r) => r.artifacts ?? []),
    enabled: Boolean(projectId && runId),
    refetchInterval: () => pollIntervalMs(5_000),
  });
}

export function useExecutionSteps(projectId: string, runId?: string) {
  return useQuery({
    queryKey: engineeringKeys.steps(projectId, runId ?? ''),
    queryFn: () =>
      engFetch<{ steps: ExecutionStep[] }>(`/engineering/execution/${runId}/steps${q(projectId)}`).then(
        (r) => r.steps ?? [],
      ),
    enabled: Boolean(projectId && runId),
    refetchInterval: () => pollIntervalMs(5_000),
  });
}

export function useExecutionTimeline(projectId: string, runId?: string) {
  return useQuery({
    queryKey: engineeringKeys.timeline(projectId, runId ?? ''),
    queryFn: () =>
      engFetch<{ timeline: TimelineEntry[] }>(
        `/engineering/execution/${runId}/timeline${q(projectId)}`,
      ).then((r) => r.timeline ?? []),
    enabled: Boolean(projectId && runId),
    refetchInterval: () => pollIntervalMs(5_000),
  });
}

export function useSessionTimeline(projectId: string, sessionId?: string) {
  return useQuery({
    queryKey: engineeringKeys.sessionTimeline(projectId, sessionId ?? ''),
    queryFn: () =>
      engFetch<{ timeline: TimelineEntry[] }>(
        `/engineering/sessions/${sessionId}/timeline${q(projectId)}`,
      ).then((r) => r.timeline ?? []),
    enabled: Boolean(projectId && sessionId),
    refetchInterval: () => pollIntervalMs(5_000),
  });
}

export function useSessionValidation(projectId: string, sessionId?: string) {
  return useQuery({
    queryKey: engineeringKeys.validation(projectId, sessionId ?? ''),
    queryFn: () =>
      engFetch<ValidationPipelineView>(`/engineering/sessions/${sessionId}/validation${q(projectId)}`),
    enabled: Boolean(projectId && sessionId),
    refetchInterval: () => pollIntervalMs(8_000),
  });
}

export function useSessionChangeset(projectId: string, sessionId?: string) {
  return useQuery({
    queryKey: engineeringKeys.changeset(projectId, sessionId ?? ''),
    queryFn: () =>
      engFetch<RepositoryChangeset>(`/engineering/sessions/${sessionId}/changeset${q(projectId)}`),
    enabled: Boolean(projectId && sessionId),
    refetchInterval: () => pollIntervalMs(10_000),
  });
}

export function useSessionAgents(projectId: string, sessionId?: string) {
  return useQuery({
    queryKey: engineeringKeys.agents(projectId, sessionId ?? ''),
    queryFn: () =>
      engFetch<{ agents: EngineeringAgentView[] }>(
        `/engineering/sessions/${sessionId}/agents${q(projectId)}`,
      ).then((r) => r.agents ?? []),
    enabled: Boolean(projectId && sessionId),
    refetchInterval: () => pollIntervalMs(4_000),
  });
}

export function useSessionTasks(projectId: string, sessionId?: string) {
  return useQuery({
    queryKey: ['engineering', 'tasks', projectId, sessionId] as const,
    queryFn: () =>
      engFetch<{ tasks: EngineeringTaskView[] }>(
        `/engineering/sessions/${sessionId}/tasks${q(projectId)}`,
      ).then((r) => r.tasks ?? []),
    enabled: Boolean(projectId && sessionId),
  });
}

export function useCreateEngineeringSession(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; description?: string; goal?: string }) =>
      engFetch<EngineeringSession>('/engineering/sessions', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          title: body.title,
          description: body.description ?? body.goal,
          source: { kind: 'manual_request', label: body.title },
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['engineering', 'sessions', projectId] }),
  });
}

export function useStartEngineeringPipeline(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const plan = await engFetch<ExecutionPlan>('/engineering/plans', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId, session_id: sessionId }),
      });
      const orch = await engFetch<OrchestrationRun>('/engineering/orchestration', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          session_id: sessionId,
          plan_id: plan.plan_id,
        }),
      });
      const run = await engFetch<ExecutionRun>('/engineering/execution', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          session_id: sessionId,
          plan_id: plan.plan_id,
          orchestration_id: orch.orchestration_id,
        }),
      });
      return { plan, orch, run };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['engineering'] });
    },
  });
}

export function useSessionAction(projectId: string, sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: 'pause' | 'resume' | 'cancel') =>
      engFetch(`/engineering/sessions/${sessionId}/${action}${q(projectId)}`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: engineeringKeys.session(projectId, sessionId) });
      qc.invalidateQueries({ queryKey: engineeringKeys.sessions(projectId) });
    },
  });
}
